import React, { useMemo, useState } from 'react';
import { Calculator, HeartPulse, Scale, Target, Activity, Droplets } from 'lucide-react';
import './style.scss';

const activityFactors = [
	{ label: 'Sedentary', value: 1.2 },
	{ label: 'Light', value: 1.375 },
	{ label: 'Moderate', value: 1.55 },
	{ label: 'Active', value: 1.725 },
	{ label: 'Athlete', value: 1.9 },
];

const getBmiStatus = (bmi) => {
	if (!bmi) return { label: 'Enter height and weight', tone: 'muted' };
	if (bmi < 18.5) return { label: 'Underweight', tone: 'warning' };
	if (bmi < 25) return { label: 'Normal', tone: 'success' };
	if (bmi < 30) return { label: 'Overweight', tone: 'warning' };
	return { label: 'Obese', tone: 'danger' };
};

const numberValue = (value) => Number(value) || 0;

const GymTools = () => {
	const [metrics, setMetrics] = useState({
		height: '',
		weight: '',
		age: '',
		gender: 'male',
		activity: 1.55,
		goalWeight: '',
		weeks: '',
	});

	const updateMetric = (key, value) => {
		setMetrics((prev) => ({ ...prev, [key]: value }));
	};

	const results = useMemo(() => {
		const height = numberValue(metrics.height);
		const weight = numberValue(metrics.weight);
		const age = numberValue(metrics.age);
		const goalWeight = numberValue(metrics.goalWeight);
		const weeks = numberValue(metrics.weeks);
		const heightM = height / 100;
		const bmi = height && weight ? weight / (heightM * heightM) : 0;
		const bmr = height && weight && age
			? metrics.gender === 'male'
				? 10 * weight + 6.25 * height - 5 * age + 5
				: 10 * weight + 6.25 * height - 5 * age - 161
			: 0;
		const tdee = bmr ? bmr * Number(metrics.activity) : 0;
		const proteinMin = weight ? weight * 1.6 : 0;
		const proteinMax = weight ? weight * 2.2 : 0;
		const water = weight ? weight * 35 / 1000 : 0;
		const weeklyChange = goalWeight && weeks ? (goalWeight - weight) / weeks : 0;
		const dailyCalorieDelta = weeklyChange ? (weeklyChange * 7700) / 7 : 0;

		return {
			bmi,
			bmr,
			tdee,
			proteinMin,
			proteinMax,
			water,
			weeklyChange,
			targetCalories: tdee ? tdee + dailyCalorieDelta : 0,
		};
	}, [metrics]);

	const bmiStatus = getBmiStatus(results.bmi);

	return (
		<div className="gym-tools-page">
			<div className="gym-tools-header">
				<div>
					<p className="eyebrow">Gym utilities</p>
					<h1>Fitness Calculators</h1>
					<p>Quick client estimates for BMI, calories, protein, hydration, and goal planning.</p>
				</div>
				<div className="header-icon">
					<Calculator size={28} />
				</div>
			</div>

			<div className="tools-grid">
				<section className="tool-panel input-panel">
					<div className="panel-title">
						<Scale size={18} />
						<span>Client Metrics</span>
					</div>

					<div className="field-grid">
						<label>
							<span>Height (cm)</span>
							<input type="number" value={metrics.height} onChange={(e) => updateMetric('height', e.target.value)} placeholder="175" />
						</label>
						<label>
							<span>Weight (kg)</span>
							<input type="number" value={metrics.weight} onChange={(e) => updateMetric('weight', e.target.value)} placeholder="72" />
						</label>
						<label>
							<span>Age</span>
							<input type="number" value={metrics.age} onChange={(e) => updateMetric('age', e.target.value)} placeholder="28" />
						</label>
						<label>
							<span>Gender</span>
							<select value={metrics.gender} onChange={(e) => updateMetric('gender', e.target.value)}>
								<option value="male">Male</option>
								<option value="female">Female</option>
							</select>
						</label>
						<label>
							<span>Activity</span>
							<select value={metrics.activity} onChange={(e) => updateMetric('activity', e.target.value)}>
								{activityFactors.map((option) => (
									<option key={option.value} value={option.value}>{option.label}</option>
								))}
							</select>
						</label>
						<label>
							<span>Goal Weight (kg)</span>
							<input type="number" value={metrics.goalWeight} onChange={(e) => updateMetric('goalWeight', e.target.value)} placeholder="68" />
						</label>
						<label>
							<span>Goal Timeline (weeks)</span>
							<input type="number" value={metrics.weeks} onChange={(e) => updateMetric('weeks', e.target.value)} placeholder="12" />
						</label>
					</div>
				</section>

				<section className="tool-panel bmi-panel">
					<div className="panel-title">
						<HeartPulse size={18} />
						<span>BMI Result</span>
					</div>
					<div className="bmi-score">{results.bmi ? results.bmi.toFixed(1) : '--'}</div>
					<div className={`status-pill ${bmiStatus.tone}`}>{bmiStatus.label}</div>
					<div className="bmi-scale">
						<span />
						<span />
						<span />
						<span />
					</div>
					<p className="helper-text">BMI is a screening estimate. Combine it with measurements and coaching judgment.</p>
				</section>
			</div>

			<div className="results-grid">
				<ResultCard icon={<Activity size={20} />} label="BMR" value={results.bmr ? `${Math.round(results.bmr)} kcal` : '--'} note="Resting daily burn" />
				<ResultCard icon={<Target size={20} />} label="TDEE" value={results.tdee ? `${Math.round(results.tdee)} kcal` : '--'} note="Maintenance calories" />
				<ResultCard icon={<Target size={20} />} label="Goal Calories" value={results.targetCalories ? `${Math.round(results.targetCalories)} kcal` : '--'} note="Based on target timeline" />
				<ResultCard icon={<Scale size={20} />} label="Weekly Change" value={results.weeklyChange ? `${results.weeklyChange.toFixed(2)} kg` : '--'} note="Required pace" />
				<ResultCard icon={<HeartPulse size={20} />} label="Protein" value={results.proteinMin ? `${Math.round(results.proteinMin)}-${Math.round(results.proteinMax)} g` : '--'} note="1.6-2.2 g/kg body weight" />
				<ResultCard icon={<Droplets size={20} />} label="Water" value={results.water ? `${results.water.toFixed(1)} L` : '--'} note="Baseline hydration target" />
			</div>
		</div>
	);
};

const ResultCard = ({ icon, label, value, note }) => (
	<div className="result-card">
		<div className="result-icon">{icon}</div>
		<div>
			<p>{label}</p>
			<strong>{value}</strong>
			<span>{note}</span>
		</div>
	</div>
);

export default GymTools;
